import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Download, ExternalLink, Code, Sparkles, Star, Zap } from 'lucide-react';

const Hero = () => {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    // Create floating particles
    const particleCount = 50;
    const newParticles = Array.from({ length: particleCount }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1,
      duration: Math.random() * 10 + 10,
      delay: Math.random() * 5
    }));
    setParticles(newParticles);
  }, []);

  const scrollToProjects = () => {
    const projectsSection = document.getElementById('projects');
    if (projectsSection) {
      projectsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToAbout = () => {
    const aboutSection = document.getElementById('about');
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const downloadResume = () => {
    const link = document.createElement('a');
    link.href = '/resume.pdf';
    link.download = 'Dev_Bhushan_Bhatta_Resume.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Premium Background Effects */}
      <div className="absolute inset-0">
        {/* Enhanced dark gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B0F1A] via-[#0a0d14] to-[#0B0F1A]"></div>
        
        {/* Additional dark overlay for more luxurious feel */}
        <div className="absolute inset-0 bg-black/20"></div>
        
        {/* Floating gradient blobs */}
        <motion.div
          className="gradient-blob absolute top-1/4 left-1/4"
          animate={{
            x: [0, 30, -20, 0],
            y: [0, -30, 20, 0],
            scale: [1, 1.1, 0.9, 1]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="gradient-blob absolute bottom-1/4 right-1/4"
          animate={{
            x: [0, -30, 20, 0],
            y: [0, 30, -20, 0],
            scale: [1, 0.9, 1.1, 1]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        />
        
        {/* Animated particles */}
        <div className="particles">
          {particles.map((particle) => (
            <motion.div
              key={particle.id}
              className="particle"
              style={{
                left: `${particle.x}%`,
                top: `${particle.y}%`,
                width: `${particle.size}px`,
                height: `${particle.size}px`
              }}
              animate={{
                y: [0, -1000],
                opacity: [0, 1, 1, 0],
                scale: [0, 1, 1, 0]
              }}
              transition={{
                duration: particle.duration,
                repeat: Infinity,
                delay: particle.delay,
                ease: "linear"
              }}
            />
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 text-center px-4 max-w-6xl mx-auto pt-20">
        {/* Greeting Badge */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="inline-flex items-center gap-3 glass-effect px-6 py-3 rounded-full mb-8"
        >
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
            className="text-2xl"
          >
            👋
          </motion.div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary-gold" />
            <span className="text-primary-gold font-medium">Available for opportunities</span>
            <Star className="w-4 h-4 text-primary-gold" />
          </div>
        </motion.div>

        {/* Main Heading */}
        <motion.h1
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="text-5xl md:text-7xl lg:text-8xl font-black mb-6 leading-tight hero-name"
        >
          <span className="text-primary-text">Hello I am</span>
          <br />
          <span className="gradient-text whitespace-nowrap">Dev Bhushan Bhatta</span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-xl md:text-2xl lg:text-3xl text-primary-muted mb-8 font-light leading-relaxed max-w-4xl mx-auto"
        >
          A passionate <span className="text-gradient font-semibold">Software Engineering Student</span> from Nepal
        </motion.p>

        {/* Punch Line */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="glass-effect p-8 rounded-2xl mb-12 max-w-4xl mx-auto"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Code className="w-6 h-6 text-primary-gold" />
            <Zap className="w-6 h-6 text-primary-gold" />
            <Code className="w-6 h-6 text-primary-gold" />
          </div>
          <p className="text-lg md:text-xl text-primary-text leading-relaxed">
            I build <span className="text-gradient font-semibold">smart, beautiful, and functional</span> web applications. 
            Currently exploring <span className="text-gradient font-semibold">full-stack development, AI & IoT magic</span>.
          </p>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-24"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={downloadResume}
            className="btn-primary group flex items-center gap-3"
          >
            <Download className="w-5 h-5 group-hover:animate-bounce" />
            Download Resume
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={scrollToProjects}
            className="btn-secondary group flex items-center gap-3"
          >
            <ExternalLink className="w-5 h-5 group-hover:rotate-12 transition-transform" />
            Visit My Projects
          </motion.button>
        </motion.div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-2xl mx-auto"
        >
          {[
            // // { number: "10+", label: "Projects Built", icon: Code },
            // { number: "5+", label: "Technologies", icon: Sparkles },
            // { number: "2+", label: "Years Learning", icon: Star },
            // { number: "100%", label: "Passionate", icon: Zap }
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 1.2 + index * 0.1 }}
              className="text-center group"
            >
              <div className="flex items-center justify-center gap-2 mb-2">
                <stat.icon className="w-5 h-5 text-primary-gold group-hover:scale-110 transition-transform" />
                <div className="text-2xl md:text-3xl font-bold text-gradient">
                  {stat.number}
                </div>
              </div>
              <div className="text-sm text-primary-muted font-medium">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
        className="absolute bottom-12 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="flex flex-col items-center gap-2 cursor-pointer"
          onClick={scrollToAbout}
        >
          <span className="text-primary-muted text-sm font-medium">Scroll to explore</span>
          <ChevronDown className="w-6 h-6 text-primary-gold" />
        </motion.div>
      </motion.div>

      {/* Floating Elements */}
      <motion.div
        className="absolute top-20 right-20 hidden lg:block"
        animate={{ rotate: 360 }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
      >
        <div className="w-32 h-32 border border-primary-gold/20 rounded-full"></div>
      </motion.div>

      <motion.div
        className="absolute bottom-20 left-20 hidden lg:block"
        animate={{ rotate: -360 }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
      >
        <div className="w-24 h-24 border border-primary-gold/30 rounded-full"></div>
      </motion.div>
    </section>
  );
};

export default Hero;